import { useMemo, useState } from "react";
import { useLocation, type NavigateOptions } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Folder,
  Home,
} from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { AppNavigationLink } from "@/components/app-navigation-link/AppNavigationLink";
import type { AppNavigationTarget } from "@/lib/navigation/appNavigation";
import { backToWorkspaceSearchState } from "@/lib/navigation/workspaceReturn";
import { SCROLL_ANCHOR_ATTRIBUTE } from "@/pages/workspace/hooks/useSearchScrollRestoration";
import { cn } from "@/lib/utils";
import {
  WorkspaceContentSearchDocument,
  WorkspaceContentSearchWorkspace,
} from "@/models/workspace/WorkspaceContentSearch";
import { buildContentSearchTree } from "./contentSearchTree";
import { ContentSearchDocumentRow } from "./ContentSearchDocumentRow";
import { documentCountLabel } from "./contentSearchLabels";

interface ContentSearchWorkspaceCardProps {
  workspace: WorkspaceContentSearchWorkspace;
  searchTerm: string;
  /** Zoomed in: every hit of this workspace is listed, so no "Vis alle" link and no zoom affordance. */
  zoomed: boolean;
  onZoom: (workspaceId: string) => void;
  /** Documents with a kildeklargøring run in flight. */
  indexingDocumentIds: Set<string>;
  onReindex: (document: WorkspaceContentSearchDocument) => void;
}

/**
 * One step of the staircase (LAR-235): the workspace as a card, its matching documents
 * grouped under the folder that holds them, and — when only the top hits are shown — a
 * link that zooms into the workspace to see them all.
 */
export const ContentSearchWorkspaceCard = ({
  workspace,
  searchTerm,
  zoomed,
  onZoom,
  indexingDocumentIds,
  onReindex,
}: ContentSearchWorkspaceCardProps) => {
  const [expanded, setExpanded] = useState(true);
  const [collapsedFolders, setCollapsedFolders] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const toggleFolder = (key: string) =>
    setCollapsedFolders((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const groups = useMemo(
    () => buildContentSearchTree(workspace.documents),
    [workspace.documents]
  );

  // Carries the search into the workspace's own search field, so the reader does not
  // type it again there, and gives the workspace a way back to this search as it
  // stands: the page keeps its term, view and zoom in the address.
  const filesTarget: AppNavigationTarget = {
    kind: "workspaceFiles",
    workspaceId: workspace.id,
    search: searchTerm,
  };
  const location = useLocation();
  const backToSearch: NavigateOptions = {
    state: backToWorkspaceSearchState(`${location.pathname}${location.search}`),
  };
  const hiddenHits =
    workspace.matchingDocumentCount - workspace.documents.length;

  return (
    <section
      aria-label={workspace.name}
      className="overflow-hidden rounded-lg border border-border bg-card"
      {...{ [SCROLL_ANCHOR_ATTRIBUTE]: workspace.id }}
    >
      <div
        className={cn(
          "flex items-center gap-2 bg-muted/50 px-3 py-2",
          expanded && "border-b border-border"
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6 shrink-0 text-muted-foreground"
          aria-label={expanded ? "Fold workspace sammen" : "Fold workspace ud"}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? (
            <ChevronDown className="size-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="size-4" aria-hidden="true" />
          )}
        </Button>
        {zoomed ? (
          <span className="min-w-0 truncate text-sm font-semibold">
            {workspace.name}
          </span>
        ) : (
          <button
            type="button"
            className="min-w-0 truncate text-left text-sm font-semibold hover:underline"
            onClick={() => onZoom(workspace.id)}
          >
            {workspace.name}
          </button>
        )}
        <span className="grow" />
        <span className="shrink-0 text-xs text-muted-foreground">
          {documentCountLabel(workspace.matchingDocumentCount)}
        </span>
        <Button
          asChild
          variant="outline"
          size="sm"
          className="ml-2 h-7 shrink-0 gap-1 px-2 text-xs"
        >
          <AppNavigationLink to={filesTarget} navigateOptions={backToSearch}>
            Åbn workspace
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </AppNavigationLink>
        </Button>
      </div>

      {expanded && (
        <div className="flex flex-col gap-0.5 px-3 pb-2.5 pt-1.5">
          {groups.map((group) => {
            // Root documents get a heading of their own ("Hjem", as the files tab calls the
            // workspace root): without it they read as more hits of the folder above.
            const isRoot = group.folderPath.length === 0;
            const toggleName = isRoot
              ? "Hjem"
              : `mappen ${group.folderPath[group.folderPath.length - 1].name}`;
            const folderExpanded = !collapsedFolders.has(group.key);
            return (
              <div key={group.key} className="flex flex-col gap-0.5">
                <div className="flex h-8 min-w-0 items-center gap-1.5 px-1.5 text-sm">
                  {/* -mx-1 gives the 24px button the 16px slot the chevron always had, so
                      the folder icon and the staircase line stay where they were. */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="-mx-1 size-6 shrink-0 text-muted-foreground"
                    aria-label={
                      folderExpanded
                        ? `Fold ${toggleName} sammen`
                        : `Fold ${toggleName} ud`
                    }
                    aria-expanded={folderExpanded}
                    onClick={() => toggleFolder(group.key)}
                  >
                    {folderExpanded ? (
                      <ChevronDown className="size-4" aria-hidden="true" />
                    ) : (
                      <ChevronRight className="size-4" aria-hidden="true" />
                    )}
                  </Button>
                  {isRoot ? (
                    <>
                      <Home
                        className="size-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <span className="font-medium">Hjem</span>
                    </>
                  ) : (
                    <>
                      <Folder
                        className="size-4 shrink-0 text-warning"
                        aria-hidden="true"
                      />
                      <span className="flex min-w-0 items-center gap-1 truncate">
                        {group.folderPath.map((folder, index) => {
                          const last = index === group.folderPath.length - 1;
                          return (
                            <span
                              key={folder.id}
                              className="flex min-w-0 items-center gap-1"
                            >
                              <span
                                className={cn(
                                  "truncate",
                                  last ? "font-medium" : "text-muted-foreground"
                                )}
                              >
                                {folder.name}
                              </span>
                              {!last && (
                                <ChevronRight
                                  className="size-3.5 shrink-0 text-muted-foreground"
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          );
                        })}
                      </span>
                    </>
                  )}
                  <span className="ml-1 shrink-0 text-xs text-muted-foreground">
                    {documentCountLabel(group.documents.length)}
                  </span>
                </div>
                {folderExpanded && (
                  <div
                    className={cn(
                      "ml-[29px] flex flex-col gap-0.5 border-l pl-3",
                      group.hasStaleIndex
                        ? "border-warning/60"
                        : "border-border"
                    )}
                  >
                    {group.documents.map((document) => (
                      <ContentSearchDocumentRow
                        key={document.id}
                        document={document}
                        searchTerm={searchTerm}
                        isIndexing={indexingDocumentIds.has(document.id)}
                        onReindex={onReindex}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {hiddenHits > 0 && !zoomed && (
            <button
              type="button"
              className="mt-0.5 inline-flex h-8 items-center gap-1 self-start pl-[30px] pr-2 text-sm font-medium text-primary hover:underline"
              onClick={() => onZoom(workspace.id)}
            >
              Vis alle {workspace.matchingDocumentCount} dokumenter i{" "}
              {workspace.name}
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </button>
          )}

          {hiddenHits > 0 && zoomed && (
            <p className="mt-0.5 pl-[30px] text-xs text-muted-foreground">
              Viser {workspace.documents.length} af{" "}
              {workspace.matchingDocumentCount} dokumenter. Indsnævr søgningen
              for at se resten.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default ContentSearchWorkspaceCard;
